import { useMemo } from "react";
import { useApp } from "@/store/AppStore";
import type { EncargoStatus } from "@/types";
import { formatCOP, formatDate } from "@/utils/format";

const STATUS_META: Record<EncargoStatus, { label: string; cls: string; icon: string }> = {
  pendiente: { label: "Pendiente", cls: "bg-secondary-500/20 text-secondary-100", icon: "ri-time-line" },
  entregado: { label: "Entregado", cls: "bg-accent-500/20 text-accent-200", icon: "ri-checkbox-circle-line" },
  cancelado: { label: "Cancelado", cls: "bg-primary-500/20 text-primary-100", icon: "ri-close-circle-line" },
};

interface AdvisorEncargoListProps {
  advisorId: string;
}

// Lista de pedidos por encargo del asesor con su estado y, cuando ya fue
// entregado, el desglose que le interesa: compra o consignación, lo que le
// costó el producto y su fee logístico.
const AdvisorEncargoList = ({ advisorId }: AdvisorEncargoListProps) => {
  const { data } = useApp();

  const list = useMemo(
    () => data.encargos.filter((e) => e.advisorId === advisorId),
    [data.encargos, advisorId]
  );

  if (list.length === 0) {
    return (
      <div className="glass-panel rounded-lg py-10 text-center">
        <span className="mx-auto w-10 h-10 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500 mb-2">
          <i className="ri-inbox-archive-line text-lg" />
        </span>
        <p className="text-xs text-foreground-500">Aún no has hecho pedidos por encargo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {list.map((e) => {
        const meta = STATUS_META[e.status];
        const delivered = e.status === "entregado" && !!e.deliverMode;
        const goodsTotal = (e.unitCostAdvisor ?? 0) * e.qty;
        return (
          <div key={e.id} className="glass-panel rounded-lg p-4 animate-fade-up">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}
              >
                <i className={meta.icon} /> {meta.label}
              </span>
              <span className="text-xs font-bold text-foreground-950">{e.qty} und</span>
              <span className="text-xs text-foreground-700 break-words">{e.productName}</span>
              <span className="text-[10px] text-foreground-500 ml-auto whitespace-nowrap">
                {formatDate(e.date)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <i className={e.delivery === "domicilio" ? "ri-truck-line" : "ri-store-2-line"} />
                {e.delivery === "domicilio" ? "Domicilio" : "Recojo"}
              </span>
              <span className="font-semibold text-foreground-800">
                Total público {formatCOP(e.unitPrice * e.qty + e.logisticsFee)}
              </span>
            </div>

            {delivered ? (
              <>
                <div className="mt-3 pt-3 border-t border-background-300/25 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                    <p className="text-[10px] text-foreground-500">Tipo de entrega</p>
                    <p className="text-xs font-bold text-foreground-950">
                      {e.deliverMode === "compra" ? "Compra (pagada)" : "Consignación"}
                    </p>
                  </div>
                  <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                    <p className="text-[10px] text-foreground-500">Lo que te costó</p>
                    <p className="text-xs font-bold text-foreground-950">{formatCOP(goodsTotal)}</p>
                  </div>
                  <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
                    <p className="text-[10px] text-accent-300">Fee logístico</p>
                    <p className="text-xs font-bold text-accent-200">
                      {e.logisticsFee > 0 ? formatCOP(e.logisticsFee) : "—"}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-foreground-500 mt-1.5 flex items-center gap-1.5">
                  <i
                    className={
                      e.deliverMode === "compra"
                        ? "ri-checkbox-circle-line text-accent-300"
                        : "ri-hand-heart-line text-secondary-200"
                    }
                  />
                  {e.deliverMode === "compra"
                    ? "Compra pagada · el producto ya es tuyo."
                    : "En consignación · pagas al venderlo."}
                </p>
              </>
            ) : (
              e.status === "pendiente" && (
                <p className="text-[11px] text-foreground-600 mt-2 flex items-center gap-1.5">
                  <i className="ri-information-line text-accent-300" /> Juanka todavía no ha entregado
                  este pedido.
                </p>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdvisorEncargoList;